import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../../store/store';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../../components/buttons/IconButton';
import { formatNumber, getTooltipId } from '../../../../utils/utils';
import { Tooltip } from 'react-tooltip';
import ChipsList from '../../../../components/common/ChipsList';
import RegisterStatusBadge from '../../../../components/common/RegisterStatusBadge';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { PROFILER_SEARCH_RESULTS_COLUMNS } from '../constants/constants';
import { Fragment } from 'react';
import { openInNewTabOnModifier } from '../../../../utils/openInNewTab';
import OwnershipSummaryCell from '../../../../components/profiler/ownership/OwnershipSummaryCell';

const CompanyResult = observer(
  ({
    company,
    displayDetails,
    excludeResult,
    isInExcludeAction = false,
    profilerColumns,
    searchId,
    searchKey,
    // TD-2456 — clickable Industry / Value chain / Product portfolio pills.
    // `onPillClick(columnKey, value)` toggles the value as a keyword filter;
    // `columnFilters` is the live filter state, read to render the active style.
    onPillClick = null,
    columnFilters = null,
    ownershipDisplayBasis = 'direct',
    ownershipSearchedBasis = 'direct',
    ownershipSearchedStructures
  }) => {
    const { utilsStore, profilerProjectStore, profilerAccessStore } = useStore();
    const state = useLocalObservable(() => ({
      nameTooltipId: getTooltipId(),
      grossProfitTooltipId: getTooltipId(),
      basicRevenueTooltipId: getTooltipId(),
      basicEmployeesTooltipId: getTooltipId()
    }));

    const isInWatchlist =
      profilerProjectStore.watchlist.findIndex((c) => c.id === company.id) !== -1;

    const watchlistButtonTooltipText = !profilerAccessStore.canViewProfilerWatchlist
      ? 'Requires Profiler Lite or Profiler Full access. Contact support@transact.digital for assistance.'
      : isInWatchlist
        ? 'Remove from watchlist'
        : 'Add to watchlist';

    const onWatchlistButtonClick = () => {
      profilerProjectStore.addToWatchlist(company);
    };

    const openDetailsInNewTab = () =>
      utilsStore.openCompanyDetails(company.id, {
        ref: 'search results',
        searchKey: searchKey || undefined,
        searchId: !searchKey && searchId ? searchId : undefined
      });

    // Cmd/Ctrl-click opens a new tab; a plain click opens details in place. The
    // right-click "Open in new tab" menu shares the same openDetailsInNewTab thunk.
    const onOpenDetails = openInNewTabOnModifier(openDetailsInNewTab, () =>
      displayDetails(company)
    );

    const profilerColumnsJSX = {
      [PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_NAME]: (
        <div
          className="col name"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_NAME
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Company name</div>
          <div className="value">
            <span
              id={state.nameTooltipId}
              className="company-name-link"
              onClick={onOpenDetails}
            >
              {company.name || PUNCTUATION.EMPTY_VALUE}
            </span>
            <Tooltip
              anchorSelect={`#${state.nameTooltipId}`}
              multiline={true}
              variant="dark"
              place={utilsStore.windowWidth >= 834 ? 'bottom-center' : 'bottom-end'}
              opacity={1}
              content={'View details'}
              className={'company-name-view-details-tooltip'}
            />
            <RegisterStatusBadge company={company} />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.LOCATION]: (
        <div
          className="col headquarter"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.LOCATION
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Location</div>
          <div className="value">{company.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_DESCRIPTION]: (() => {
        const descriptionTooltipId = getTooltipId();
        return (
          <div
            className="col company-description"
            style={
              utilsStore.windowWidth > 833
                ? {
                    minWidth: profilerColumns.find(
                      (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_DESCRIPTION
                    ).minWidth
                  }
                : {}
            }
          >
            <div className="label">Company description</div>
            <div className="value description-with-tooltip">
              {utilsStore.windowWidth > 833 && !!company.description && (
                <>
                  <div
                    id={descriptionTooltipId}
                    className="default-black-info-circle-wrap description-similar-companies-button"
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                  </div>
                  <Tooltip
                    anchorSelect={`#${descriptionTooltipId}`}
                    multiline={true}
                    variant="light"
                    place="bottom-center"
                    opacity={1}
                    content={company.description}
                    className={'description-similar-companies-tooltip'}
                    clickable
                  />
                </>
              )}
              <div className="short-description" style={{ flex: 1 }}>
                {company.description || PUNCTUATION.EMPTY_VALUE}
              </div>
            </div>
          </div>
        );
      })(),

      [PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION]: (() => {
        const portfolioTooltipId = getTooltipId();
        return (
          <div
            className="col product-portfolio-description"
            style={
              utilsStore.windowWidth > 833
                ? {
                    minWidth: profilerColumns.find(
                      (column) =>
                        column.id === PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION
                    ).minWidth
                  }
                : {}
            }
          >
            <div className="label">Product portfolio</div>
            <div className="value description-with-tooltip">
              {utilsStore.windowWidth > 833 && !!company.productPortfolioDescription && (
                <>
                  <div
                    id={portfolioTooltipId}
                    className="default-black-info-circle-wrap description-tooltip-icon"
                  >
                    <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                  </div>
                  <Tooltip
                    anchorSelect={`#${portfolioTooltipId}`}
                    multiline={true}
                    variant="light"
                    place="bottom-center"
                    opacity={1}
                    content={company.productPortfolio}
                    className={'description-similar-companies-tooltip'}
                    clickable
                  />
                </>
              )}
              <div className="short-description description-text">
                {company.productPortfolio || PUNCTUATION.EMPTY_VALUE}
              </div>
            </div>
          </div>
        );
      })(),

      [PROFILER_SEARCH_RESULTS_COLUMNS.KEY_PRODUCTS]: (
        <div
          className="col key-products"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.KEY_PRODUCTS
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Key products</div>
          <div className="value">
            <ChipsList list={company.keyProducts} propertyToMap="name" />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.KEY_END_MARKETS]: (
        <div
          className="col key-end-markets"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.KEY_END_MARKETS
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Key end-markets</div>
          <div className="value">
            <ChipsList list={company.businessVerticals} propertyToMap="name" />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY]: (
        <div
          className="col industry"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Industry</div>
          <div className="value">
            <ChipsList
              list={company.industries}
              onChipClick={onPillClick ? (value) => onPillClick('industries', value) : null}
              activeValues={columnFilters?.industries?.keywords ?? null}
              {...{
                ...(utilsStore.windowWidth > 833 &&
                !!profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 833,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-search-listing > .listing-layout > .results > .item > .cols',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .col'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN]: (
        <div
          className="col value-chain"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Value chain</div>
          <div className="value">
            <ChipsList
              list={company.valueChain}
              onChipClick={onPillClick ? (value) => onPillClick('valueChain', value) : null}
              activeValues={columnFilters?.valueChain?.keywords ?? null}
              {...{
                ...(utilsStore.windowWidth > 833 &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 833,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-search-listing > .listing-layout > .results > .item > .cols',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .col'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCTS]: (
        <div
          className="col products"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCTS
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Products</div>
          <div className="value">
            <ChipsList
              list={company.products}
              onChipClick={onPillClick ? (value) => onPillClick('products', value) : null}
              activeValues={columnFilters?.products?.keywords ?? null}
              {...{
                ...(utilsStore.windowWidth > 833 &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY && column.selected
                ) &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 833,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-search-listing > .listing-layout > .results > .item > .cols',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .col'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.SALES]: (
        <div
          className="col revenue"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.SALES
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Sales (€M)</div>
          <div className="value">
            {company.revenue ? (
              <>
                {!!company.isRevenueGrossProfit && (
                  <>
                    <div id={state.grossProfitTooltipId} className="default-black-info-circle-wrap">
                      <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                    </div>
                    <Tooltip
                      anchorSelect={`#${state.grossProfitTooltipId}`}
                      multiline={true}
                      variant="dark"
                      place="bottom-center"
                      opacity={1}
                      content={
                        <>
                          This company was included
                          <br />
                          based on reported gross profit,
                          <br />
                          not revenue.
                        </>
                      }
                      className={'default-black-info-tooltip-classname'}
                    />
                  </>
                )}
                <font
                  id={state.basicRevenueTooltipId}
                  style={company.isRevenueBasic ? { color: '#348cf9' } : {}}
                >
                  {company.revenue}
                </font>
                <span className="year" style={company.isRevenueBasic ? { color: '#348cf9' } : {}}>
                  &lsquo;
                  {company.revenueYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>
                {!!company.isRevenueBasic && (
                  <Tooltip
                    anchorSelect={`#${state.basicRevenueTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>Based on self-disclosure or estimate</>}
                    className={'default-black-info-tooltip-classname'}
                  />
                )}
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.EMPLOYEES]: (
        <div
          className="col employees"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.EMPLOYEES
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Employees</div>
          <div className="value">
            {!isNaN(company.employees) && company.employees !== null ? (
              <>
                <font
                  id={state.basicEmployeesTooltipId}
                  style={company.isEmployeesBasic ? { color: '#348cf9' } : {}}
                >
                  {formatNumber((+company.employees).toFixed(2))}
                </font>
                <span className="year" style={company.isEmployeesBasic ? { color: '#348cf9' } : {}}>
                  &lsquo;
                  {company.employeesYear?.toString?.()?.slice?.(-2) || PUNCTUATION.EMPTY_VALUE}
                </span>
                {!!company.isEmployeesBasic && (
                  <Tooltip
                    anchorSelect={`#${state.basicEmployeesTooltipId}`}
                    multiline={true}
                    variant="dark"
                    place="bottom-center"
                    opacity={1}
                    content={<>Based on self-disclosure or estimate</>}
                    className={'default-black-info-tooltip-classname'}
                  />
                )}
              </>
            ) : (
              PUNCTUATION.EMPTY_VALUE
            )}
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.CAGR3Y]: (
        <div
          className="col cagr"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.CAGR3Y
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">CAGR 3Y (%)</div>
          <div className="value">{company.cagr3Y ? company.cagr3Y : PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.CAGR4Y]: (
        <div
          className="col cagr"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.CAGR4Y
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">CAGR 4Y (%)</div>
          <div className="value">{company.cagr4Y ? company.cagr4Y : PUNCTUATION.EMPTY_VALUE}</div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.EBIT_MARGIN]: (
        <div
          className="col ebit-margin"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.EBIT_MARGIN
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">Ebit Margin (%)</div>
          <div className="value">
            {company.ebitPercentage ? company.ebitPercentage : PUNCTUATION.EMPTY_VALUE}
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.SITUATION]: (
        <div
          className="col situation"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.SITUATION
                  ).minWidth
                }
              : {}
          }
        >
          <div className="label">Situation</div>
          <div className="value">
            <ChipsList
              list={company.situation}
              {...{
                ...(utilsStore.windowWidth > 833 &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY && column.selected
                ) &&
                !profilerColumns.find(
                  (column) =>
                    column.id === PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN && column.selected
                )
                  ? {
                      alignTopMeasurementsEnabled: true,
                      alignTopMeasurementsMinResolutionWidth: 833,
                      alignTopMeasurementsRowQuerySelector:
                        '.page-search-listing > .listing-layout > .results > .item > .cols',
                      alignTopMeasurementsItemsContainersQuerySelector: '> .col'
                    }
                  : {})
              }}
            />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.SHAREHOLDERS]: (
        <div
          className="col shareholders"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.SHAREHOLDERS
                  )?.minWidth
                }
              : {}
          }
        >
          <div className="label">Shareholders</div>
          <div className="value">
            {/* The basis switch lives in the column header, which phones do not show. */}
            <OwnershipSummaryCell
              summary={company.ownershipSummary}
              basis={utilsStore.windowWidth > 833 ? ownershipDisplayBasis : ownershipSearchedBasis}
              searchedBasis={ownershipSearchedBasis}
              searchedStructures={ownershipSearchedStructures}
            />
          </div>
        </div>
      ),
      [PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS]: (
        <div
          className="col no-border actions profiler-search-actions"
          style={
            utilsStore.windowWidth > 833
              ? {
                  minWidth: profilerColumns.find(
                    (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS
                  ).minWidthResult
                }
              : {}
          }
        >
          {company.isExcluded ? (
            <IconButton
              wrapClassName="search-results-restore-btn"
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.ARROW_COUNTERCLOCKWISE}
              innerText="Restore"
              onClick={() => excludeResult(company, false)}
              disabled={isInExcludeAction}
              iconOnRight
              filled
            />
          ) : (
            <>
              <IconButton
                wrapClassName="search-results-view-details-button desktop-only"
                type={ICON_BUTTON_TYPES.BLUE}
                onClick={onOpenDetails}
                icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
                tooltipText="View details"
                linkContextMenu={[
                  {
                    icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                    text: 'Open in new tab',
                    onClick: () => {
                      utilsStore.openCompanyDetails(company.id, {
                        ref: 'search results',
                        searchKey: searchKey || undefined,
                        searchId: !searchKey && searchId ? searchId : undefined
                      });
                    }
                  }
                ]}
              />
              <IconButton
                wrapClassName="search-results-view-details-button mobile-only"
                filled
                type={ICON_BUTTON_TYPES.BLUE}
                onClick={onOpenDetails}
                innerText="View details"
                linkContextMenu={[
                  {
                    icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                    text: 'Open in new tab',
                    onClick: () => {
                      utilsStore.openCompanyDetails(company.id, {
                        ref: 'search results',
                        searchKey: searchKey || undefined,
                        searchId: !searchKey && searchId ? searchId : undefined
                      });
                    }
                  }
                ]}
              />
              <IconButton
                wrapClassName="search-results-watchlist-action-btn"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.WATCHLIST}
                disabled={
                  profilerProjectStore.isLoadingWatchlist ||
                  profilerProjectStore.initialLoadWatchlist ||
                  !!profilerProjectStore.companiesInWatchlistAction[company.id] ||
                  !profilerAccessStore.canViewProfilerWatchlist
                }
                tooltipText={watchlistButtonTooltipText}
                filled={isInWatchlist}
                onClick={onWatchlistButtonClick}
              />
              <IconButton
                wrapClassName="search-results-exclude-btn"
                type={ICON_BUTTON_TYPES.RED}
                icon={ICON_BUTTON_ICONS.X_LG}
                tooltipText="Exclude result"
                onClick={() => excludeResult(company, true)}
                disabled={isInExcludeAction}
                tooltipPosition={
                  utilsStore.windowWidth >= 834 && utilsStore.windowWidth <= 1200
                    ? 'bottom-end'
                    : 'bottom-center'
                }
              />
            </>
          )}
        </div>
      )
      // THE BELOW IS COMMENTED OUT AS IT'S TO BE IMPLEMENTED ON BACKEND, ONCE IMPLEMENTED THERE, JUST UNCOMMENT
      // [PROFILER_SEARCH_RESULTS_COLUMNS.TOP_MANAGEMENT]: (
      //   <div
      //     className="col top-management"
      //     style={
      //       utilsStore.windowWidth > 833
      //         ? {
      //             minWidth: profilerColumns.find(
      //               (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.TOP_MANAGEMENT
      //             ).minWidth
      //           }
      //         : {}
      //     }>
      //     <div className="label">Top management</div>
      //     <div className="value">
      //       <ChipsList list={company.topManagement} propertyToMap="name" />
      //     </div>
      //   </div>
      // )
    };

    return (
      <div
        key={company.id}
        className={`item funds-search-item ${company.isExcluded ? 'no-shadow' : ''}`}
      >
        <div
          className="cols profiler-search-cols"
          style={
            utilsStore.windowWidth > 833
              ? {
                  /*
                   * ROW GRID TEMPLATE CALCULATION
                   * ==============================
                   * Calculates the CSS grid template for table rows based on selected columns.
                   * Must match the header filtering logic in index.jsx for proper alignment.
                   */
                  gridTemplateColumns: profilerColumns
                    .filter((column) => column.selected)
                    .map((column) =>
                      column.id === PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS
                        ? column.fragmentResult
                        : column.fragment
                    )
                    .join(' ')
                }
              : {}
          }
        >
          {company.showScoring && (
            <>
              <div
                className="result-scoring-badge noselect search-listing-layout"
                id={`result-scoring-badge-${company.id}`}
                style={{ backgroundColor: company.scoreSecondaryColor }}
              >
                <div className="score-icon-wrap">
                  <img
                    src={ICON_BUTTON_ICONS.EXCLAMATAION_CIRCLE}
                    style={{ filter: company.scoreIconFilter }}
                  />
                </div>
                <div className="score-label" style={{ color: company.scorePrimaryColor }}>
                  {company.scoreLabel}
                </div>
              </div>
              <Tooltip
                anchorSelect={`#result-scoring-badge-${company.id}`}
                multiline={true}
                variant="light"
                place="bottom-end"
                opacity={1}
                content={company.scoreTooltipText}
                className={company.scoreTooltipClassName}
                clickable
              />
            </>
          )}

          {
            /*
             * ROW CONTENT FILTERING
             * =====================
             * Filters and displays column data in each row based on user selection.
             * Must match the header filtering logic in index.jsx.
             */
            profilerColumns
              .filter((column) => column.selected)

              .map((column) => {
                return <Fragment key={column.id}>{profilerColumnsJSX[column.id]}</Fragment>;
              })
          }
        </div>
      </div>
    );
  }
);

export default CompanyResult;
